/**
 * Le dessin d'un coupon cadeau, et rien d'autre : aucune base de données ici.
 *
 * ## Pourquoi le PDF se fabrique dans le NAVIGATEUR
 *
 * Les factures et les notes de crédit sont produites par une fonction edge et
 * déposées dans un bucket public : qui connaît l'URL lit le document. C'est
 * acceptable pour une facture, qui ne vaut rien pour un tiers.
 *
 * **Un bon cadeau, si.** C'est un instrument au porteur : une URL qui fuit vaut
 * une semaine de stage gratuite. Le coupon est donc dessiné sur le poste de
 * l'administration, à partir de codes qu'elle a déjà le droit de lire, et il
 * n'est déposé nulle part. Rien à protéger, rien à nettoyer, et l'impression
 * est immédiate.
 *
 * L'envoi par courriel, lui, a besoin d'un serveur SMTP : le navigateur
 * fabrique le PDF et le confie à une fonction edge qui ne fait que l'envoyer.
 */
import { PDFDocument, StandardFonts, degrees, rgb } from 'pdf-lib';
import type { PDFFont, PDFImage, PDFPage } from 'pdf-lib';

/* --------------------------------------------------------------------------
   Les couleurs, relevees sur les coupons de mai 2026
   --------------------------------------------------------------------------
   Elles ne sont pas approchees : elles ont ete echantillonnees pixel par pixel
   sur le rendu des SVG d'origine, pour que l'impression d'aujourd'hui se pose
   sans jurer a cote de celle d'hier.
*/
export interface Teinte { talon: [number, number, number]; corps: [number, number, number] }

const hex = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16) / 255,
  parseInt(h.slice(3, 5), 16) / 255,
  parseInt(h.slice(5, 7), 16) / 255,
];

export const TEINTES: Teinte[] = [
  { talon: hex('#FFCFDF'), corps: hex('#F494B4') },   // rose
  { talon: hex('#FFE1AB'), corps: hex('#F9B233') },   // orange
  { talon: hex('#CBEAFD'), corps: hex('#38B6FF') },   // bleu
  { talon: hex('#D0EDD7'), corps: hex('#00BF63') },   // vert
];

const CREME = hex('#FAF0E6');
const NOIR = rgb(0, 0, 0);
const BLANC = rgb(1, 1, 1);

/* --------------------------------------------------------------------------
   Le texte qu'une police WinAnsi sait ecrire
   --------------------------------------------------------------------------
   `StandardFonts.Helvetica` encode en cp1252 et `drawText` **leve** sur un
   caractere absent — ce qui, sur une note de credit, avait deja empeche 43
   familles de recevoir leur document.

   Les libelles des bons sont saisis par l'administration : un nom d'ecole
   polonais ou un tiret cadratin colle depuis un traitement de texte suffisent.
   On translittere donc, plutot que d'echouer.
*/
const REMPLACEMENTS: Record<string, string> = {
  '‘': "'", '’': "'", '‚': ',', '“': '"', '”': '"',
  '–': '-', '—': '-', '…': '...', ' ': ' ', ' ': ' ',
  'ł': 'l', 'Ł': 'L', 'ś': 's', 'Ś': 'S', 'ż': 'z', 'Ż': 'Z', 'ź': 'z', 'Ź': 'Z',
  'ć': 'c', 'Ć': 'C', 'ń': 'n', 'Ń': 'N', 'ą': 'a', 'Ą': 'A', 'ę': 'e', 'Ę': 'E',
  'č': 'c', 'Č': 'C', 'š': 's', 'Š': 'S', 'ř': 'r', 'Ř': 'R', 'ě': 'e', 'Ě': 'E',
  'ů': 'u', 'Ů': 'U', 'ğ': 'g', 'Ğ': 'G', 'ı': 'i', 'İ': 'I', 'ș': 's', 'Ș': 'S',
  'ț': 't', 'Ț': 'T', 'ā': 'a', 'ē': 'e', 'ī': 'i', 'ū': 'u', 'ő': 'o', 'ű': 'u',
};

/** Ce que WinAnsi accepte au-dela de l'ASCII, et qu'il ne faut pas abimer. */
const WINANSI_HAUT = new Set(
  'ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÑÒÓÔÕÖØÙÚÛÜÝßàáâãäåæçèéêëìíîïñòóôõöøùúûüýÿ€£¥§°²³·«»'.split(''),
);

export function latin1(texte: string): string {
  let sortie = '';
  for (const c of texte ?? '') {
    if (REMPLACEMENTS[c] !== undefined) { sortie += REMPLACEMENTS[c]; continue; }
    const code = c.codePointAt(0)!;
    if (code < 128 || WINANSI_HAUT.has(c)) { sortie += c; continue; }
    /*
      Dernier recours : on decompose et on jette les diacritiques. Mieux vaut
      « Krakow » que rien du tout, et infiniment mieux qu'une exception.
    */
    const nu = c.normalize('NFD').replace(/[̀-ͯ]/g, '');
    sortie += nu.length && nu.codePointAt(0)! < 128 ? nu : '';
  }
  return sortie;
}

/* -------------------------------------------------------------------------- */

export interface Bon {
  code: string;
  /** La ligne principale : ce que le bon offre. */
  valeur: string;
  /** La periode de validite, en une ligne, telle qu'elle sera imprimee. */
  validite: string;
  /** L'astérisque du bas : ce que le bon couvre reellement. */
  mention: string;
}

const A4 = { l: 595.28, h: 841.89 };
const MARGE_X = 39;
const MARGE_Y = 34;
const COUPON_H = 180;
const ENTRE_DEUX = 18;
const TALON_L = 131;
const PERFO_X = 137;
const RAYON = 10;

/** Combien de coupons par page. Quatre, comme sur les planches existantes. */
export const PAR_PAGE = 4;

/*
  Le contour d'un ticket : un rectangle arrondi avec deux encoches
  semi-circulaires sur les cotes, a hauteur de la perforation.

  pdf-lib ne dessine pas d'encoche : on pose le rectangle, puis on redessine
  deux disques de la couleur du FOND par-dessus. C'est le procede des tickets
  imprimes, et il a l'avantage d'etre exact au pixel.
*/
function ticket(
  page: PDFPage, x: number, y: number, l: number, h: number,
  couleur: [number, number, number], fond: [number, number, number],
) {
  /*
    Un rectangle arrondi se compose : deux rectangles en croix, puis quatre
    disques aux coins. pdf-lib n'a pas de rayon de bordure, et cette
    construction est exacte — aucun pixel ne depasse.
  */
  page.drawRectangle({
    x: x + RAYON, y, width: l - 2 * RAYON, height: h, color: rgb(...couleur),
  });
  page.drawRectangle({
    x, y: y + RAYON, width: l, height: h - 2 * RAYON, color: rgb(...couleur),
  });
  for (const [cx, cy] of [
    [x + RAYON, y + RAYON], [x + l - RAYON, y + RAYON],
    [x + RAYON, y + h - RAYON], [x + l - RAYON, y + h - RAYON],
  ]) page.drawCircle({ x: cx, y: cy, size: RAYON, color: rgb(...couleur) });

  /* Les deux encoches laterales, a hauteur de perforation. */
  const yEncoche = y + h / 2;
  page.drawCircle({ x, y: yEncoche, size: 7, color: rgb(...fond) });
  page.drawCircle({ x: x + l, y: yEncoche, size: 7, color: rgb(...fond) });
}

/**
 * Coupe un texte en lignes qui tiennent dans `largeur`.
 *
 * Utilisee aussi pour le texte VERTICAL du talon, ou « largeur » est en fait
 * la hauteur disponible : une rotation ne change pas la mesure.
 */
function lignes(texte: string, police: PDFFont, taille: number, largeur: number): string[] {
  const mots = latin1(texte).split(/\s+/).filter(Boolean);
  const out: string[] = [];
  let courante = '';
  for (const mot of mots) {
    const essai = courante ? `${courante} ${mot}` : mot;
    if (police.widthOfTextAtSize(essai, taille) > largeur && courante) {
      out.push(courante);
      courante = mot;
    } else {
      courante = essai;
    }
  }
  if (courante) out.push(courante);
  return out;
}

function dessinerCoupon(
  page: PDFPage, bon: Bon, rang: number,
  gras: PDFFont, normal: PDFFont, mascotte: PDFImage,
) {
  const teinte = TEINTES[rang % TEINTES.length];
  const fond: [number, number, number] = [1, 1, 1];
  const y = A4.h - MARGE_Y - (rang + 1) * COUPON_H - rang * ENTRE_DEUX;
  const largeurTotale = A4.l - 2 * MARGE_X;

  /* --- Le talon, clair --- */
  ticket(page, MARGE_X, y, TALON_L, COUPON_H, teinte.talon, fond);

  /* --- Le corps, sature --- */
  const xCorps = MARGE_X + PERFO_X + 6;
  const lCorps = largeurTotale - PERFO_X - 6;
  ticket(page, xCorps, y, lCorps, COUPON_H, teinte.corps, fond);

  /*
    --- La perforation ---
    En GRIS, et non en blanc : sur fond blanc, des tirets blancs ne se voient
    pas. C'est la ligne qui dit ou dechirer — elle doit se lire.
  */
  for (let d = y + 10; d < y + COUPON_H - 10; d += 11) {
    page.drawRectangle({
      x: MARGE_X + PERFO_X + 1, y: d, width: 1.6, height: 6,
      color: rgb(0.72, 0.72, 0.72),
    });
  }

  /* --- Le talon : « BON », la validite, et LE CODE --- */
  page.drawText('BON', {
    x: MARGE_X + 34, y: y + 34, size: 30, font: gras, color: NOIR, rotate: degrees(90),
  });
  /*
    La validite, ecrite verticalement. Elle DEBORDAIT : une phrase de soixante
    caracteres a 6,4 points mesure deux cents points, pour cent cinquante-six
    disponibles — le texte sortait du coupon et mordait sur le suivant.

    On la coupe donc en deux colonnes et on reduit la taille jusqu'a ce
    qu'elle tienne. Un coupon dont la date de validite deborde est un coupon
    qu'on ne peut pas opposer a une famille.
  */
  {
    const hauteurUtile = COUPON_H - 22;
    const texte = latin1(bon.validite);
    let taille = 6.4;
    let colonnes = lignes(texte, normal, taille, hauteurUtile);
    while (colonnes.length > 2 && taille > 4.4) {
      taille -= 0.2;
      colonnes = lignes(texte, normal, taille, hauteurUtile);
    }
    /* Au-dela de deux colonnes, on tronque plutot que d'empieter sur le code. */
    colonnes = colonnes.slice(0, 2);
    colonnes.forEach((colonne, i) => {
      page.drawText(colonne, {
        x: MARGE_X + 50 + i * (taille + 2.5), y: y + 11,
        size: taille, font: normal, color: NOIR,
        rotate: degrees(90),
      });
    });
  }

  /*
    Le code, dans un cartouche blanc. C'est la seule chose que le parent devra
    retaper : il est donc le plus gros element du talon, et espace pour que
    chaque caractere se detache.
  */
  const codeEspace = latin1(bon.code).split('').join(' ');
  const tailleCode = 13;
  const hauteurCode = gras.widthOfTextAtSize(codeEspace, tailleCode);
  page.drawRectangle({
    x: MARGE_X + 78, y: y + (COUPON_H - hauteurCode) / 2 - 9,
    width: 23, height: hauteurCode + 18, color: BLANC,
  });
  page.drawText(codeEspace, {
    x: MARGE_X + 94, y: y + (COUPON_H - hauteurCode) / 2,
    size: tailleCode, font: gras, color: NOIR, rotate: degrees(90),
  });

  /* --- Le corps : la banderole « BON POUR » --- */
  const yBanderole = y + COUPON_H - 42;
  page.drawRectangle({
    x: xCorps + 22, y: yBanderole, width: 168, height: 26, color: rgb(...CREME),
  });
  page.drawText('BON POUR', {
    x: xCorps + 62, y: yBanderole + 8, size: 13, font: gras, color: NOIR,
  });

  /* --- Le cercle clair et la mascotte --- */
  const cx = xCorps + lCorps - 78;
  const cy = y + COUPON_H / 2;
  page.drawCircle({ x: cx, y: cy, size: 56, color: rgb(...teinte.talon) });
  const hMascotte = 78;
  const lMascotte = hMascotte * (mascotte.width / mascotte.height);
  page.drawImage(mascotte, {
    x: cx - lMascotte / 2, y: cy - hMascotte / 2, width: lMascotte, height: hMascotte,
  });

  /* --- Ce que le bon offre --- */
  const largeurValeur = lCorps - 190;
  let taille = 17;
  let l = lignes(bon.valeur, gras, taille, largeurValeur);
  /* On reduit jusqu'a tenir : un coupon tronque ne se rattrape pas a l'impression. */
  while (l.length > 4 && taille > 10) {
    taille -= 1;
    l = lignes(bon.valeur, gras, taille, largeurValeur);
  }
  const hBloc = l.length * (taille + 4);
  let yLigne = cy + hBloc / 2 - taille;
  for (const ligne of l) {
    page.drawText(ligne, { x: xCorps + 26, y: yLigne, size: taille, font: gras, color: NOIR });
    yLigne -= taille + 4;
  }

  /* --- La mention du bas --- */
  if (bon.mention) {
    const m = latin1(bon.mention);
    const taillem = 6.6;
    page.drawText(m, {
      x: xCorps + lCorps - 12 - normal.widthOfTextAtSize(m, taillem),
      y: y + 9, size: taillem, font: normal, color: NOIR,
    });
  }
}

/**
 * La planche de coupons, en PDF.
 *
 * Quatre par page, comme les planches existantes : c'est la disposition qui se
 * decoupe le plus vite, et celle que l'administration connait deja.
 */
export async function planche(bons: Bon[], mascottePng: Uint8Array): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const gras = await pdf.embedFont(StandardFonts.HelveticaBold);
  const normal = await pdf.embedFont(StandardFonts.Helvetica);
  const mascotte = await pdf.embedPng(mascottePng);

  pdf.setTitle('Bons cadeaux - Eclosion ASBL');
  pdf.setProducer('Eclosion ASBL');

  for (let i = 0; i < bons.length; i += PAR_PAGE) {
    const page = pdf.addPage([A4.l, A4.h]);
    bons.slice(i, i + PAR_PAGE).forEach((bon, rang) => {
      dessinerCoupon(page, bon, rang, gras, normal, mascotte);
    });
  }

  return await pdf.save();
}
